import { useEffect, useRef, useState } from "react";
import { Play, X } from "lucide-react";
import type { VideoItem } from "@/lib/site";

const ytThumb = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
const ytEmbed = (id: string) => `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`;

/** Muted looping background video. Skips autoplay on small screens / reduced data. */
export const BackgroundVideo = ({ src, poster, className }: { src?: string; poster?: string; className?: string }) => {
  const [play, setPlay] = useState(false);
  useEffect(() => {
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    setPlay(!!src && window.innerWidth >= 768 && !conn?.saveData);
  }, [src]);
  if (play) return <video className={className} src={src} poster={poster} autoPlay muted loop playsInline preload="metadata" aria-hidden />;
  if (poster) return <img className={className} src={poster} alt="" aria-hidden />;
  return <div className={`${className} bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.45),transparent_60%)]`} aria-hidden />;
};

/** Click to play. Nothing heavy loads until clicked. */
export const ClickToPlay = ({ video, className = "aspect-video", label }: { video: Pick<VideoItem, "youtubeId" | "src" | "poster" | "title">; className?: string; label?: string }) => {
  const [on, setOn] = useState(false);
  const poster = video.poster || (video.youtubeId ? ytThumb(video.youtubeId) : undefined);
  const has = !!(video.youtubeId || video.src);
  if (on && video.youtubeId)
    return <iframe className={`${className} w-full rounded-2xl`} src={ytEmbed(video.youtubeId)} title={video.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />;
  if (on && video.src) return <video className={`${className} w-full rounded-2xl bg-secondary`} src={video.src} poster={poster} controls autoPlay playsInline />;
  return (
    <button
      type="button"
      onClick={() => has && setOn(true)}
      className={`${className} group relative w-full overflow-hidden rounded-2xl bg-secondary`}
      aria-label={`Play ${video.title}`}
    >
      {poster ? (
        <img src={poster} alt={video.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <span className="absolute inset-0 flex items-end p-4 text-left text-sm text-background">{label || video.title}</span>
      )}
      <span className="absolute inset-0 flex items-center justify-center bg-secondary/20">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background/95 text-primary shadow-xl transition-transform group-hover:scale-110">
          <Play className="ml-1 h-7 w-7 fill-current" />
        </span>
      </span>
    </button>
  );
};

export const VideoModal = ({ video, onClose }: { video: VideoItem | null; onClose: () => void }) => {
  useEffect(() => {
    if (!video) return;
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [video, onClose]);
  if (!video) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label={video.title} className="fixed inset-0 z-[60] flex items-center justify-center bg-secondary/95 p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute right-4 top-4 rounded-full bg-background p-2 text-foreground" aria-label="Close video">
        <X className="h-5 w-5" />
      </button>
      <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        {video.youtubeId ? (
          <iframe className="aspect-video w-full rounded-2xl" src={ytEmbed(video.youtubeId)} title={video.title} allow="autoplay; encrypted-media" allowFullScreen />
        ) : video.src ? (
          <video className="aspect-video w-full rounded-2xl" src={video.src} controls autoPlay playsInline />
        ) : (
          <div className="flex aspect-video items-center justify-center rounded-2xl bg-secondary text-secondary-foreground">{video.title}</div>
        )}
      </div>
    </div>
  );
};

/** Vertical 9:16 reel. Plays muted only when on screen, never on page load. */
export const Reel = ({ src, poster, label }: { src?: string; poster?: string; label: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className="relative aspect-[9/16] w-[220px] shrink-0 snap-center overflow-hidden rounded-3xl bg-secondary md:w-[260px]">
      {src && visible ? (
        <video className="h-full w-full object-cover" src={src} poster={poster} autoPlay muted loop playsInline preload="none" />
      ) : poster ? (
        <img className="h-full w-full object-cover" src={poster} alt={label} loading="lazy" />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-background">{label}</span>
      )}
    </div>
  );
};

/** Faint, muted, looping background video. Only loads once scrolled into view. */
export const BgVideo = ({ src, className = "opacity-20", fade = false }: { src: string; className?: string; fade?: boolean }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setOn(true); io.disconnect(); } }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <video ref={ref} className={`pointer-events-none absolute inset-0 h-full w-full object-cover mix-blend-luminosity ${className}`} src={on ? src : undefined} autoPlay muted loop playsInline preload="none" aria-hidden="true" style={fade ? { maskImage: FADE, WebkitMaskImage: FADE } : undefined} />
  );
};

const FADE = "linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)";

/** Pool of all footage clips; the homepage hero picks 2 at random on every load. */
const heroPool = Object.values(
  import.meta.glob<{ url: string }>("@/assets/footage/clip-*.mp4.asset.json", { eager: true, import: "default" }),
).map((a) => a.url);
export const randomHeroPair = () => {
  const a = Math.floor(Math.random() * heroPool.length);
  let b = Math.floor(Math.random() * (heroPool.length - 1));
  if (b >= a) b++;
  return [heroPool[a], heroPool[b]];
};

/** Vertical ad with sound. Shows a poster until clicked, then plays with audio and controls. */
export const AdReel = ({ src, poster, client }: { src: string; poster: string; client: string }) => {
  const [play, setPlay] = useState(false);
  return (
    <figure>
      <div className="relative aspect-[9/16] overflow-hidden rounded-3xl bg-secondary shadow-lg">
        {play ? (
          <video className="h-full w-full object-cover" src={src} poster={poster} autoPlay controls playsInline />
        ) : (
          <button type="button" onClick={() => setPlay(true)} className="group absolute inset-0" aria-label={`Play ${client} ad with sound`}>
            <img src={poster} alt={`${client} lead gen ad`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl transition-transform group-hover:scale-110"><Play className="ml-1 h-7 w-7 fill-current" /></span>
            </span>
          </button>
        )}
      </div>
      <figcaption className="mt-3 text-center font-semibold">{client}</figcaption>
    </figure>
  );
};
